import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";
import { ToolGate } from "@/components/dashboard/ToolGate";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import WhopCallback from "./pages/WhopCallback";
import Checkout from "./pages/Checkout";
import NotFound from "./pages/NotFound";
import UploadEmailLogo from "./pages/UploadEmailLogo";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Pricing from "./pages/Pricing";
import Waitlist from "./pages/Waitlist";
import WaitlistConfirmed from "./pages/WaitlistConfirmed";
import Quiz from "./pages/Quiz";
import QuizHome from "./pages/QuizHome";
import SpecialOffer from "./pages/SpecialOffer";
import Upgrade from "./pages/Upgrade";
import Apply from "./pages/Apply";
import ThankYou from "./pages/ThankYou";


// Dashboard
import { DashboardLayout } from "./components/dashboard/DashboardLayout";
import Dashboard from "./pages/app/Dashboard";
import AdminDashboard from "./pages/app/AdminDashboard";
import Announcements from "./pages/app/Announcements";
import LiveCalls from "./pages/app/LiveCalls";
import Livestreams from "./pages/app/Livestreams";
import Recordings from "./pages/app/Recordings";
import StoryCraftFormula from "./pages/app/StoryCraftFormula";
import NexisDashboard from "./pages/app/nexis/NexisDashboard";
import NexisCreateScript from "./pages/app/nexis/NexisCreateScript";
import NexisProjectScript from "./pages/app/nexis/NexisProjectScript";
import NexisVisualStyle from "./pages/app/nexis/NexisVisualStyle";
import NexisSceneCount from "./pages/app/nexis/NexisSceneCount";
import NexisImagePrompts from "./pages/app/nexis/NexisImagePrompts";
import NexisVideoPrompts from "./pages/app/nexis/NexisVideoPrompts";
import AskFint from "./pages/app/AskFint";
import MonetizationDashboard from "./pages/app/monetization/MonetizationDashboard";
import MonetizationEntryType from "./pages/app/monetization/MonetizationEntryType";
import MonetizationInput from "./pages/app/monetization/MonetizationInput";
import MonetizationProducts from "./pages/app/monetization/MonetizationProducts";
import MonetizationAssetUpload from "./pages/app/monetization/MonetizationAssetUpload";
import MonetizationBuild from "./pages/app/monetization/MonetizationBuild";
import MonetizationPricing from "./pages/app/monetization/MonetizationPricing";
import MonetizationFormat from "./pages/app/monetization/MonetizationFormat";
import MonetizationStructure from "./pages/app/monetization/MonetizationStructure";
import General from "./pages/app/General";
import Content from "./pages/app/Content";
import Tools from "./pages/app/Tools";
import ProductWins from "./pages/app/ProductWins";
import AccessTest from "./pages/app/AccessTest";
import Settings from "./pages/app/Settings";
import Tutorials from "./pages/app/Tutorials";
import SpinWheel from "./pages/app/SpinWheel";
import Support from "./pages/app/Support";
import ClipAI from "./pages/app/ClipAI";
import KeywordResearch from "./pages/app/KeywordResearch";
import EmailListBuilder from "./pages/app/EmailListBuilder";
import SalesPageBuilder from "./pages/app/SalesPageBuilder";
import LaunchAdvisor from "./pages/app/LaunchAdvisor";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/whop/callback" element={<WhopCallback />} />
            
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/waitlist/confirmed" element={<WaitlistConfirmed />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/quiz-home" element={<QuizHome />} />
            <Route path="/special-offer" element={<SpecialOffer />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/thank-you" element={<ThankYou />} />
            
            
            {/* Utility Routes */}
            <Route path="/upload-email-logo" element={<UploadEmailLogo />} />
            

            {/* Protected Dashboard Routes */}
            <Route path="/app" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="live-calls" element={<LiveCalls />} />
              <Route path="livestreams" element={<Livestreams />} />
              <Route path="recordings" element={<Recordings />} />
              <Route path="storycraft-formula" element={<StoryCraftFormula />} />
              <Route path="tutorials" element={<Tutorials />} />
              <Route path="nexis-ai" element={<NexisDashboard />} />
              <Route path="nexis-ai/create" element={<NexisCreateScript />} />
              <Route path="nexis-ai/project/:projectId/script" element={<NexisProjectScript />} />
              <Route path="nexis-ai/project/:projectId/visual-style" element={<NexisVisualStyle />} />
              <Route path="nexis-ai/project/:projectId/scene-count" element={<NexisSceneCount />} />
              <Route path="nexis-ai/project/:projectId/image-prompts" element={<NexisImagePrompts />} />
              <Route path="nexis-ai/project/:projectId/video-prompts" element={<NexisVideoPrompts />} />
              <Route path="monetization-ai" element={<ToolGate toolKey="monetisation-ai"><MonetizationDashboard /></ToolGate>} />
              <Route path="monetization-ai/new" element={<ToolGate toolKey="monetisation-ai"><MonetizationEntryType /></ToolGate>} />
              <Route path="monetization-ai/project/:projectId/input" element={<ToolGate toolKey="monetisation-ai"><MonetizationInput /></ToolGate>} />
              <Route path="monetization-ai/project/:projectId/products" element={<ToolGate toolKey="monetisation-ai"><MonetizationProducts /></ToolGate>} />
              <Route path="monetization-ai/project/:projectId/assets/:productId" element={<ToolGate toolKey="monetisation-ai"><MonetizationAssetUpload /></ToolGate>} />
              <Route path="monetization-ai/project/:projectId/format/:productId" element={<ToolGate toolKey="monetisation-ai"><MonetizationFormat /></ToolGate>} />
              <Route path="monetization-ai/project/:projectId/structure/:productId" element={<ToolGate toolKey="monetisation-ai"><MonetizationStructure /></ToolGate>} />
              <Route path="monetization-ai/project/:projectId/build/:productId" element={<ToolGate toolKey="monetisation-ai"><MonetizationBuild /></ToolGate>} />
              <Route path="monetization-ai/project/:projectId/pricing/:productId" element={<ToolGate toolKey="monetisation-ai"><MonetizationPricing /></ToolGate>} />
              <Route path="askfint" element={<AskFint />} />
              <Route path="general" element={<General />} />
              <Route path="content" element={<Content />} />
              <Route path="tools" element={<Tools />} />
              <Route path="wins" element={<ProductWins />} />
              <Route path="access-test" element={<AccessTest />} />
              <Route path="settings" element={<Settings />} />
              <Route path="support" element={<Support />} />
              <Route path="spin-wheel" element={<SpinWheel />} />
              <Route path="clip-ai" element={<ToolGate toolKey="clipping-ai"><ClipAI /></ToolGate>} />
              <Route path="keywords" element={<KeywordResearch />} />
              <Route path="email-list-builder" element={<ToolGate toolKey="email-list-builder"><EmailListBuilder /></ToolGate>} />
              <Route path="sales-page-builder" element={<ToolGate toolKey="sales-page-builder"><SalesPageBuilder /></ToolGate>} />
              <Route path="launch-advisor" element={<ToolGate toolKey="launch-advisor"><LaunchAdvisor /></ToolGate>} />
              {/* Redirects for broken URLs */}
              <Route path="spin-the-wheel" element={<Navigate to="/app/spin-wheel" replace />} />
              <Route path="ask-fint" element={<Navigate to="/app/askfint" replace />} />
              <Route path="keyword-research" element={<Navigate to="/app/keywords" replace />} />
              <Route path="admin" element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              } />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
